<template>
  <div class="container-fluid pt-2">

    <!-- Header Row -->
    <div class="row">
      <div class="col-md-4 col-sm-12">
        <div class="text-left py-3">
          <h3 class="text-weight-900 text-primary">Discount Campaigns</h3>
        </div>
      </div>
      <div class="col-md-8 col-sm-12">
        <div class="d-flex justify-content-end py-3">
          <button class="btn btn-sm btn-outline-primary mx-1" @click="openCreate">
            <i class="bi bi-plus-lg mr-1"></i> New Campaign
          </button>
        </div>
      </div>
    </div>

    <!-- Search & Filter -->
    <div class="row py-1">
      <div class="col-md-6 col-sm-12 py-1">
        <div class="input-group w-100 my-1">
          <input
            type="text"
            class="form-control"
            v-model="searchQuery"
            placeholder="Search campaigns…"
            @input="onSearch"
          />
          <div class="input-group-append">
            <button class="btn btn-outline-secondary" type="button">
              <i class="fa fa-search"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-sm-12 py-1">
        <select class="form-control" v-model="filterStatus" @change="fetchCampaigns(1)">
          <option value="">All statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
        </select>
      </div>
    </div>

    <!-- Table -->
    <div style="margin-bottom: 5em;">
      <b-table
        id="campaigns-table"
        :items="campaigns"
        :fields="fields"
        striped
        hover
        small
        sticky-header="100vh"
        sort-icon-left
        responsive="sm"
        :busy="loading"
      >
        <template #table-busy>
          <div class="text-center py-4">
            <span class="spinner-border spinner-border-sm mr-2"></span> Loading…
          </div>
        </template>

        <template #empty>
          <div class="text-center text-muted py-4">No campaigns found.</div>
        </template>

        <template #cell(name)="row">
          <div>{{ row.item.name }}</div>
          <small v-if="row.item.description" class="text-muted">{{ row.item.description | truncate(60) }}</small>
        </template>

        <template #cell(status)="row">
          <span :class="statusBadge(row.item.status)" class="badge">{{ row.item.status }}</span>
        </template>

        <template #cell(starts_at)="row">
          <small>{{ row.item.starts_at | shortDate }}</small>
        </template>

        <template #cell(ends_at)="row">
          <small>{{ row.item.ends_at | shortDate }}</small>
        </template>

        <template #cell(actions)="row">
          <div class="dropdown">
            <button
              class="btn btn-outline-secondary btn-sm dropdown-toggle"
              type="button"
              data-toggle="dropdown"
              data-offset="-100,3"
            >
              Actions
            </button>
            <div class="dropdown-menu dropdown-menu-right">
              <a class="dropdown-item" href="#" @click.prevent="openEdit(row.item)">Edit</a>
              <div class="dropdown-divider"></div>
              <a class="dropdown-item" :href="'/discount-campaigns/' + row.item.id + '/products'">
                Assign Products
              </a>
              <div class="dropdown-divider"></div>
              <a
                v-if="row.item.status === 'active'"
                class="dropdown-item"
                href="#"
                @click.prevent="pauseCampaign(row.item)"
              >Pause</a>
              <a
                v-if="row.item.status === 'paused'"
                class="dropdown-item"
                href="#"
                @click.prevent="resumeCampaign(row.item)"
              >Resume</a>
              <div
                v-if="row.item.status === 'active' || row.item.status === 'paused'"
                class="dropdown-divider"
              ></div>
              <a class="dropdown-item text-danger" href="#" @click.prevent="confirmDelete(row.item)">Delete</a>
            </div>
          </div>
        </template>
      </b-table>

      <b-pagination
        v-if="totalRows > perPage"
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        @input="fetchCampaigns"
      />
    </div>

    <!-- Create / Edit modal -->
    <discount-campaign-form
      modal-id="campaign-form-modal"
      :campaign="editingCampaign"
      @saved="onSaved"
    />

    <!-- Delete confirm modal -->
    <b-modal
      ref="deleteModal"
      title="Delete Campaign"
      @ok="deleteCampaign"
      ok-variant="danger"
      ok-title="Delete"
    >
      <p>Delete <strong>{{ deletingCampaign && deletingCampaign.name }}</strong>? This cannot be undone.</p>
    </b-modal>

  </div>
</template>

<script>
import axios from 'axios';
import DiscountCampaignForm from './DiscountCampaignForm.vue';

export default {
  name: 'DiscountCampaigns',
  components: { DiscountCampaignForm },
  filters: {
    shortDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    truncate(val, len) {
      if (!val) return '';
      return val.length > len ? val.substring(0, len) + '…' : val;
    },
  },
  data() {
    return {
      campaigns:        [],
      loading:          false,
      searchQuery:      '',
      filterStatus:     '',
      editingCampaign:  null,
      deletingCampaign: null,
      currentPage:      1,
      perPage:          20,
      totalRows:        0,
      searchTimer:      null,
      fields: [
        { key: 'name',                    label: 'Campaign',  sortable: true },
        { key: 'status',                  label: 'Status',    sortable: true },
        { key: 'starts_at',               label: 'Start',     sortable: true },
        { key: 'ends_at',                 label: 'End',       sortable: true },
        { key: 'campaign_products_count', label: 'Products',  sortable: true, class: 'text-center' },
        { key: 'created_by_name',         label: 'Created by' },
        { key: 'actions',                 label: '',          thStyle: { width: '110px' } },
      ],
    };
  },
  mounted() {
    this.fetchCampaigns();
  },
  methods: {
    async fetchCampaigns(page) {
      if (page) this.currentPage = page;
      this.loading = true;
      try {
        const params = { page: this.currentPage, per_page: this.perPage };
        if (this.filterStatus)          params.status = this.filterStatus;
        if (this.searchQuery.trim())    params.search = this.searchQuery.trim();

        const { data } = await axios.get('/api/discount-campaigns', { params });
        this.campaigns   = (data.data || []).map(c => ({
          ...c,
          created_by_name: c.created_by?.name || '—',
        }));
        this.totalRows   = data.total        || 0;
        this.currentPage = data.current_page || 1;
      } catch { /* silently fail */ } finally {
        this.loading = false;
      }
    },

    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchCampaigns(1), 300);
    },

    openCreate() {
      this.editingCampaign = null;
      this.$bvModal.show('campaign-form-modal');
    },

    openEdit(campaign) {
      this.editingCampaign = { ...campaign };
      this.$bvModal.show('campaign-form-modal');
    },

    onSaved() {
      this.fetchCampaigns(this.currentPage);
    },

    async pauseCampaign(campaign) {
      try {
        await axios.post(`/api/discount-campaigns/${campaign.id}/pause`);
        this.fetchCampaigns(this.currentPage);
      } catch (err) {
        alert(err.response?.data?.message || 'Could not pause campaign.');
      }
    },

    async resumeCampaign(campaign) {
      try {
        await axios.post(`/api/discount-campaigns/${campaign.id}/resume`);
        this.fetchCampaigns(this.currentPage);
      } catch (err) {
        alert(err.response?.data?.message || 'Could not resume campaign.');
      }
    },

    confirmDelete(campaign) {
      this.deletingCampaign = campaign;
      this.$refs.deleteModal.show();
    },

    async deleteCampaign() {
      if (!this.deletingCampaign) return;
      try {
        await axios.delete(`/api/discount-campaigns/${this.deletingCampaign.id}`);
        this.fetchCampaigns(this.currentPage);
      } catch (err) {
        alert(err.response?.data?.message || 'Could not delete campaign.');
      } finally {
        this.deletingCampaign = null;
      }
    },

    statusBadge(status) {
      const map = {
        active:    'badge-primary',
        scheduled: 'badge-secondary',
        paused:    'badge-dark',
        completed: 'badge-secondary',
      };
      return map[status] || 'badge-secondary';
    },
  },
};
</script>
